import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { theme } from '../utils/theme';

const PrepareVideoOverlay = ({ isVisible }) => {
  const { t } = useTranslation('workflow');
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: isVisible ? 1 : 0 }}
      exit={{ opacity: 0 }}
      className="absolute inset-0 w-full h-full rounded-xl"
      style={{ pointerEvents: 'none' }}
    >
      <div className="absolute inset-0 bg-gradient-to-b from-black/60 to-black/90 backdrop-blur-sm rounded-xl">
        <div className="absolute inset-0 flex flex-col items-center justify-center text-white">
          <div className="relative">
            {/* Large Main Gear - Always animating */}
            <motion.svg
              className="w-20 h-20 text-white"
              viewBox="0 0 24 24"
              fill="currentColor"
              stroke="none"
              animate={{ rotate: 360 }}
              transition={{ duration: 4, repeat: Infinity, ease: "linear", repeatDelay: 0 }}
            >
              <path d="M12 2c-0.3 0-0.5 0.2-0.7 0.4L10 4.1c-0.5 0.1-1 0.3-1.5 0.5L7.2 3.7c-0.2-0.2-0.5-0.2-0.8-0.1l-2 1.2C4.2 5 4.1 5.3 4.2 5.6l0.6 2.2c-0.3 0.4-0.5 0.9-0.7 1.3L2 9.7c-0.3 0.1-0.5 0.3-0.5 0.6v2.4c0 0.3 0.2 0.5 0.5 0.6l2.1 0.7c0.2 0.5 0.4 0.9 0.7 1.3l-0.6 2.2c-0.1 0.3 0 0.6 0.2 0.7l2 1.2c0.2 0.1 0.5 0.1 0.8-0.1l1.3-1.1c0.5 0.2 1 0.4 1.5 0.5l0.7 2.1c0.1 0.3 0.3 0.4 0.6 0.4h2.4c0.3 0 0.5-0.2 0.7-0.4l0.7-2.1c0.5-0.1 1-0.3 1.5-0.5l1.3 1.1c0.2 0.2 0.5 0.2 0.8 0.1l2-1.2c0.3-0.2 0.4-0.5 0.2-0.7l-0.6-2.2c0.3-0.4 0.5-0.9 0.7-1.3L22 13.3c0.3-0.1 0.5-0.3 0.5-0.6v-2.4c0-0.3-0.2-0.5-0.5-0.6l-2.1-0.7c-0.2-0.5-0.4-0.9-0.7-1.3l0.6-2.2c0.1-0.3 0-0.6-0.2-0.7l-2-1.2C17.3 3.4 17 3.5 16.8 3.7l-1.3 1.1c-0.5-0.2-1-0.4-1.5-0.5L13.3 2.4C13.2 2.2 13 2 12.7 2H12zM12 8c2.2 0 4 1.8 4 4s-1.8 4-4 4s-4-1.8-4-4s1.8-4 4-4z"/>
            </motion.svg>

            {/* Medium Gear - Always animating */}
            <motion.svg
              className="w-16 h-16 text-white/90 absolute -right-6 top-0"
              viewBox="0 0 24 24"
              fill="currentColor"
              stroke="none"
              animate={{ rotate: -360 }}
              transition={{ duration: 4, repeat: Infinity, ease: "linear", repeatDelay: 0 }}
            >
              <path d="M12 2c-0.3 0-0.5 0.2-0.7 0.4L10 4.1c-0.5 0.1-1 0.3-1.5 0.5L7.2 3.7c-0.2-0.2-0.5-0.2-0.8-0.1l-2 1.2C4.2 5 4.1 5.3 4.2 5.6l0.6 2.2c-0.3 0.4-0.5 0.9-0.7 1.3L2 9.7c-0.3 0.1-0.5 0.3-0.5 0.6v2.4c0 0.3 0.2 0.5 0.5 0.6l2.1 0.7c0.2 0.5 0.4 0.9 0.7 1.3l-0.6 2.2c-0.1 0.3 0 0.6 0.2 0.7l2 1.2c0.2 0.1 0.5 0.1 0.8-0.1l1.3-1.1c0.5 0.2 1 0.4 1.5 0.5l0.7 2.1c0.1 0.3 0.3 0.4 0.6 0.4h2.4c0.3 0 0.5-0.2 0.7-0.4l0.7-2.1c0.5-0.1 1-0.3 1.5-0.5l1.3 1.1c0.2 0.2 0.5 0.2 0.8 0.1l2-1.2c0.3-0.2 0.4-0.5 0.2-0.7l-0.6-2.2c0.3-0.4 0.5-0.9 0.7-1.3L22 13.3c0.3-0.1 0.5-0.3 0.5-0.6v-2.4c0-0.3-0.2-0.5-0.5-0.6l-2.1-0.7c-0.2-0.5-0.4-0.9-0.7-1.3l0.6-2.2c0.1-0.3 0-0.6-0.2-0.7l-2-1.2C17.3 3.4 17 3.5 16.8 3.7l-1.3 1.1c-0.5-0.2-1-0.4-1.5-0.5L13.3 2.4C13.2 2.2 13 2 12.7 2H12zM12 8c2.2 0 4 1.8 4 4s-1.8 4-4 4s-4-1.8-4-4s1.8-4 4-4z"/>
            </motion.svg>

            {/* Small Gear - Always animating */}
            <motion.svg
              className="w-12 h-12 text-white/80 absolute -right-2 -bottom-4"
              viewBox="0 0 24 24"
              fill="currentColor"
              stroke="none"
              animate={{ rotate: 360 }}
              transition={{ duration: 4, repeat: Infinity, ease: "linear", repeatDelay: 0 }}
            >
              <path d="M12 2c-0.3 0-0.5 0.2-0.7 0.4L10 4.1c-0.5 0.1-1 0.3-1.5 0.5L7.2 3.7c-0.2-0.2-0.5-0.2-0.8-0.1l-2 1.2C4.2 5 4.1 5.3 4.2 5.6l0.6 2.2c-0.3 0.4-0.5 0.9-0.7 1.3L2 9.7c-0.3 0.1-0.5 0.3-0.5 0.6v2.4c0 0.3 0.2 0.5 0.5 0.6l2.1 0.7c0.2 0.5 0.4 0.9 0.7 1.3l-0.6 2.2c-0.1 0.3 0 0.6 0.2 0.7l2 1.2c0.2 0.1 0.5 0.1 0.8-0.1l1.3-1.1c0.5 0.2 1 0.4 1.5 0.5l0.7 2.1c0.1 0.3 0.3 0.4 0.6 0.4h2.4c0.3 0 0.5-0.2 0.7-0.4l0.7-2.1c0.5-0.1 1-0.3 1.5-0.5l1.3 1.1c0.2 0.2 0.5 0.2 0.8 0.1l2-1.2c0.3-0.2 0.4-0.5 0.2-0.7l-0.6-2.2c0.3-0.4 0.5-0.9 0.7-1.3L22 13.3c0.3-0.1 0.5-0.3 0.5-0.6v-2.4c0-0.3-0.2-0.5-0.5-0.6l-2.1-0.7c-0.2-0.5-0.4-0.9-0.7-1.3l0.6-2.2c0.1-0.3 0-0.6-0.2-0.7l-2-1.2C17.3 3.4 17 3.5 16.8 3.7l-1.3 1.1c-0.5-0.2-1-0.4-1.5-0.5L13.3 2.4C13.2 2.2 13 2 12.7 2H12zM12 8c2.2 0 4 1.8 4 4s-1.8 4-4 4s-4-1.8-4-4s1.8-4 4-4z"/>
            </motion.svg>
          </div>
          <p className="mt-8 text-white text-lg font-medium">{t('preparing')}</p>
        </div>
      </div>
    </motion.div>
  );
};

const DownloadOverlay = ({ isVisible }) => {
  const { t } = useTranslation('workflow');
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: isVisible ? 1 : 0 }}
      className="absolute inset-0 w-full h-full backface-hidden rounded-xl overflow-hidden"
    >
      <motion.div 
        className="absolute inset-0 rounded-xl"
        animate={{ 
          background: [
            "linear-gradient(to bottom, rgba(34, 197, 94, 0.3), rgba(34, 197, 94, 0.5))",
            "linear-gradient(to bottom, rgba(34, 197, 94, 0.5), rgba(34, 197, 94, 0.7))",
            "linear-gradient(to bottom, rgba(34, 197, 94, 0.3), rgba(34, 197, 94, 0.5))"
          ]
        }}
        transition={{ duration: 2, repeat: Infinity }}
      >
        <div className="absolute inset-0 flex flex-col items-center justify-center backdrop-blur-[2px] p-6 rounded-xl">
          <motion.div
            className="text-white flex flex-col items-center text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <motion.div
              animate={{ 
                scale: [1, 1.1, 1],
                rotate: [0, -5, 5, -5, 0]
              }}
              transition={{ duration: 2, repeat: Infinity, repeatDelay: 1 }}
            >
              <svg 
                className="w-16 h-16 drop-shadow-lg" 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M12 3c-4.5 0-8.1 3.6-8.1 8.1 0 3.5 2.2 6.5 5.3 7.7M12 3c4.5 0 8.1 3.6 8.1 8.1 0 3.5-2.2 6.5-5.3 7.7m-2.8-7.7h5.6M12 16.9V11.1" 
                />
                <motion.path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15.7 14.3L12 18l-3.7-3.7"
                  initial={{ pathLength: 0, opacity: 0 }}
                  animate={{ pathLength: 1, opacity: 1 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                />
              </svg>
            </motion.div>
            <h4 className="text-2xl font-bold mb-3 drop-shadow-lg mt-4">{t('shareText')}</h4>
          </motion.div>
        </div>
      </motion.div>
    </motion.div>
  );
};

const TimelinePoint = ({ number, title, description, isActive, isComplete, onClick, isMobile }) => (
  <motion.div 
    className="relative flex items-start cursor-pointer group"
    onClick={onClick}
    whileHover={{ x: 4 }}
  >
    <div className="flex items-start">
      {/* Square box with number or checkmark - Hidden in mobile */}
      {!isMobile && (
        <div className={`w-14 h-14 flex items-center justify-center rounded-lg transition-all shadow-sm
          ${isActive 
            ? 'bg-[#6D9773] text-white shadow-md shadow-[#6D9773]/20 scale-110' 
            : isComplete
              ? 'bg-[#FFBA00] text-white'
              : 'bg-white text-[#0C3B2E] border-2 border-[#6D9773]/20 group-hover:border-[#6D9773]'
          }`}
        >
          {isComplete ? (
            <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
            </svg>
          ) : (
            <span className="text-xl font-bold">{number}</span>
          )}
        </div>
      )}

      {/* Text content */}
      <div className={`${!isMobile ? 'ml-4' : ''} pt-1`}>
        <div className="flex items-center gap-3">
          {/* Mobile marker */}
          {isMobile && (
            <div className={`w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-lg transition-colors duration-300
              ${isActive 
                ? 'bg-[#6D9773] text-white shadow-lg shadow-[#6D9773]/25' 
                : isComplete
                  ? 'bg-[#FFBA00] text-white'
                  : 'bg-white text-[#0C3B2E] border-2 border-[#6D9773]/20'
              }`}
            >
              {isComplete ? (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <span className="text-base font-bold">{number}</span>
              )}
            </div>
          )}
          <h4 className={`${theme.text.h3} text-lg mb-1 ${isActive ? 'text-[#0C3B2E]' : 'text-[#0C3B2E]/70'}`}>
            {title}
          </h4>
        </div>
        {/* Remove AnimatePresence for desktop view */}
        {isMobile ? (
          <AnimatePresence mode="wait">
            {isActive && (
              <motion.p
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className={`${theme.text.small} max-w-[240px]`}
              >
                {description}
              </motion.p>
            )}
          </AnimatePresence>
        ) : (
          <p className={`${theme.text.small} max-w-[240px] text-[#0C3B2E]/70`}>
            {description}
          </p>
        )}
      </div>
    </div>
  </motion.div>
);

const ScrollIndicator = ({ direction, onClick }) => (
  <button 
    onClick={onClick}
    className={`${theme.button.base} ${theme.button.primary} absolute ${direction === 'left' ? 'left-0' : 'right-0'} top-1/2 -translate-y-1/2 z-10 ${direction === 'left' ? 'ml-2' : 'mr-2'} md:${direction === 'left' ? 'ml-4' : 'mr-4'} w-8 h-8 md:w-12 md:h-12 rounded-full flex items-center justify-center hover:bg-[#0C3B2E] opacity-60 md:opacity-100 hover:opacity-100 transition-opacity`}
  >
    <svg className="w-4 h-4 md:w-6 md:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={direction === 'left' ? "M15 19l-7-7 7-7" : "M9 5l7 7-7 7"} />
    </svg>
  </button>
);

const StepIndicator = ({ total, current, onClick }) => (
  <div className="flex justify-center gap-2 mt-4">
    {Array.from({ length: total }).map((_, index) => (
      <div
        key={index}
        onClick={() => onClick(index)}
        className={`w-2 h-2 rounded-full transition-all duration-300 cursor-pointer ${
          index === current ? 'bg-[#6D9773] w-4' : 'bg-[#6D9773]/20'
        }`}
      />
    ))}
  </div>
);

const HorizontalTimelineWorkflow = () => {
  const { t } = useTranslation('workflow');
  const [activeStep, setActiveStep] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);
  const scrollContainerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [touchStartX, setTouchStartX] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);

  const steps = [
    {
      id: 'upload',
      showVideo: false,
      showPrepare: false,
      showDownload: false
    },
    {
      id: 'personalize',
      showVideo: false,
      showPrepare: true,
      showDownload: false
    },
    {
      id: 'watch',
      showVideo: true,
      showPrepare: false,
      showDownload: false
    },
    {
      id: 'share',
      showVideo: true,
      showPrepare: false,
      showDownload: true
    }
  ];

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 1024);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Desktop-only animation timer
  useEffect(() => {
    if (!isDesktop) return;
    
    const timer = setInterval(() => {
      setActiveStep((prevStep) => (prevStep + 1) % steps.length);
    }, 5000); // Changed from 2000 to 4000 for 4-second intervals

    return () => clearInterval(timer);
  }, [steps.length, isDesktop]);

  // Update video playing state - only for desktop auto-animation
  useEffect(() => {
    if (isDesktop) {
      setIsPlaying(steps[activeStep].showVideo);
    }
  }, [activeStep, isDesktop]);

  // Mobile scroll handler with persistent animations
  const handleScroll = () => {
    if (!scrollContainerRef.current || isDesktop || isSwiping) return;
    
    const container = scrollContainerRef.current;
    const scrollLeft = container.scrollLeft;
    const containerWidth = container.offsetWidth;
    const scrollWidth = container.scrollWidth;
    
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft + containerWidth < scrollWidth);
    
    const newIndex = Math.round(scrollLeft / containerWidth);
    if (newIndex !== activeStep && newIndex >= 0 && newIndex < steps.length) {
      setActiveStep(newIndex);
      // For mobile, set playing state based on the current step
      if (!isDesktop) {
        setIsPlaying(steps[newIndex].showVideo);
      }
    }
  };

  // Handle manual navigation
  const scrollToStep = (stepIndex) => {
    if (!scrollContainerRef.current) return;
    
    const container = scrollContainerRef.current;
    const containerWidth = container.offsetWidth;
    const targetPosition = stepIndex * containerWidth;
    
    container.scrollTo({
      left: targetPosition,
      behavior: 'smooth'
    });
    
    setActiveStep(stepIndex);
    if (!isDesktop) {
      setIsPlaying(steps[stepIndex].showVideo);
    }
  };

  // Touch event handlers
  const handleTouchStart = (e) => {
    setTouchStartX(e.touches[0].clientX);
    setIsSwiping(true);
  };

  const handleTouchMove = (e) => {
    if (!isSwiping) return;
    // Allow vertical scrolling by not preventing default
  };

  const handleTouchEnd = (e) => {
    if (!isSwiping) return;
    
    const touchEndX = e.changedTouches[0].clientX;
    const diffX = touchStartX - touchEndX;
    
    // Only process if horizontal swipe is significant (more than 50px)
    if (Math.abs(diffX) > 50) {
      if (diffX > 0 && canScrollRight) {
        // Swipe left - go to next slide
        scrollToStep(Math.min(steps.length - 1, activeStep + 1));
      } else if (diffX < 0 && canScrollLeft) {
        // Swipe right - go to previous slide
        scrollToStep(Math.max(0, activeStep - 1));
      }
    }
    
    setIsSwiping(false);
  };

  // Navigation button handlers
  const handlePrevStep = () => {
    scrollToStep(Math.max(0, activeStep - 1));
  };

  const handleNextStep = () => {
    scrollToStep(Math.min(steps.length - 1, activeStep + 1));
  };

  useEffect(() => {
    // Initial scroll position check
    setTimeout(() => {
      handleScroll();
    }, 200);
  }, []);

  return (
    <div className="bg-white flex flex-col">
      {/* Header */}
      <div className="text-center pt-16 pb-8">
        <h2 className={`${theme.text.h2} text-4xl sm:text-5xl mb-6`}>
          {t('title')}{' '}
          <span className="bg-gradient-to-r from-[#6D9773] to-[#0C3B2E] bg-clip-text text-transparent ml-2">
            {t('titleHighlight')}
          </span>
        </h2>
        <p className={`${theme.text.body} max-w-2xl mx-auto`}>
          {t('description')}
        </p>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex items-center">
        <div className="max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid lg:grid-cols-12 gap-6">
            {/* Desktop Timeline */}
            <div className="hidden lg:flex lg:col-span-5">
              <div className="w-full flex flex-col justify-center gap-12 pr-16">
                {steps.map((step, index) => (
                  <TimelinePoint
                    key={index}
                    number={index + 1}
                    title={t(`steps.${step.id}.title`)}
                    description={t(`steps.${step.id}.description`)}
                    isActive={index === activeStep}
                    isComplete={index < activeStep}
                    onClick={() => setActiveStep(index)}
                    isMobile={false}
                  />
                ))}
              </div>
            </div>

            {/* Mobile View - Full Width Cards */}
            <div className="lg:hidden w-full overflow-hidden relative">
              <div className="relative">
                {canScrollLeft && <ScrollIndicator direction="left" onClick={handlePrevStep} />}
                <div 
                  ref={scrollContainerRef}
                  className="overflow-x-auto hide-scrollbar mx-auto"
                  style={{
                    scrollSnapType: 'x mandatory',
                    WebkitOverflowScrolling: 'touch'
                  }}
                  onScroll={handleScroll}
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                >
                  <div className="flex w-full px-4">
                    {steps.map((step, index) => (
                      <motion.div
                        key={index}
                        className="flex-none w-full min-w-[280px] px-4"
                        style={{ scrollSnapAlign: 'center' }}
                        initial={false}
                        animate={{ 
                          scale: index === activeStep ? 1 : 0.95,
                          opacity: index === activeStep ? 1 : 0.7
                        }}
                        transition={{ duration: 0.2 }}
                      >
                        <div className="max-w-[280px] mx-auto flex flex-col items-center">
                          {/* Mobile View - Video Container */}
                          <div className="w-[280px] h-[380px] rounded-2xl shadow-lg overflow-hidden mb-6">
                            <div className="relative w-full h-full">
                              {/* Base Image */}
                              <img
                                src="/showcase/showcase_image.jpeg"
                                alt="Original"
                                className="absolute inset-0 w-full h-full object-cover"
                              />

                              {/* Prepare Overlay */}
                              <PrepareVideoOverlay isVisible={step.showPrepare} />

                              {/* Video Layer */}
                              {(isDesktop || (step.showVideo && index === activeStep)) && (
                                <motion.div 
                                  initial={{ opacity: 0 }}
                                  animate={{ opacity: 1 }}
                                  className="absolute inset-0"
                                >
                                  <video
                                    className="w-full h-full object-cover"
                                    autoPlay={true}
                                    loop
                                    muted
                                    playsInline
                                    key={`video-${index}`}
                                  >
                                    <source src="/showcase/showcase_video.mp4" type="video/mp4" />
                                  </video>
                                </motion.div>
                              )}

                              {/* Download Overlay */}
                              <DownloadOverlay isVisible={step.showDownload} />
                            </div>
                          </div>

                          {/* Text Content with fixed height container */}
                          <div className="h-[120px]">
                            <TimelinePoint
                              number={index + 1}
                              title={t(`steps.${step.id}.title`)}
                              description={t(`steps.${step.id}.description`)}
                              isActive={index === activeStep}
                              isComplete={index < activeStep}
                              onClick={() => scrollToStep(index)}
                              isMobile={true}
                            />
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
                {canScrollRight && <ScrollIndicator direction="right" onClick={handleNextStep} />}

                {/* Step Indicators */}
                <StepIndicator 
                  total={steps.length} 
                  current={activeStep} 
                  onClick={scrollToStep}
                />
              </div>

              {/* Mobile CTA Button */}
              {activeStep === steps.length - 1 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="flex justify-center mt-8"
                >
                  <a
                    href="/register"
                    className={`${theme.button.base} ${theme.button.cta} ${theme.button.sizes.lg}`}
                  >
                    {t('cta')}
                    <svg className="w-5 h-5 ml-2 inline-block" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </a>
                </motion.div>
              )}
            </div>

            {/* Desktop Visual Content */}
            <div className="hidden lg:flex lg:col-span-7 justify-center">
              <div className="flex flex-col items-center">
                <div className="w-[320px] h-[480px] rounded-2xl shadow-2xl overflow-hidden">
                  <div className="relative w-full h-full">
                    {/* Base Image */}
                    <img
                      src="/showcase/showcase_image.jpeg"
                      alt="Original"
                      className="absolute inset-0 w-full h-full object-cover"
                    />

                    {/* Prepare Overlay */}
                    <PrepareVideoOverlay isVisible={steps[activeStep].showPrepare} />

                    {/* Video Layer */}
                    {isPlaying && (
                      <motion.div 
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="absolute inset-0"
                      >
                        <video
                          className="w-full h-full object-cover"
                          autoPlay
                          loop
                          muted
                          playsInline
                        >
                          <source src="/showcase/showcase_video.mp4" type="video/mp4" />
                        </video>
                      </motion.div>
                    )}

                    {/* Download Overlay */}
                    <DownloadOverlay isVisible={steps[activeStep].showDownload} />
                  </div>
                </div>
                
                {/* Desktop CTA - Always visible */}
                <div className="mt-8">
                  <a
                    href="/register"
                    className={`${theme.button.base} ${theme.button.cta} ${theme.button.sizes.lg}`}
                  >
                    {t('cta')}
                    <svg className="w-5 h-5 ml-2 inline-block" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Add custom styles to hide scrollbar but keep functionality
const styles = `
  .hide-scrollbar {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
  .hide-scrollbar::-webkit-scrollbar {
    display: none;
  }
`;

// Add styles to head
const styleSheet = document.createElement("style");
styleSheet.innerText = styles;
document.head.appendChild(styleSheet);

export default HorizontalTimelineWorkflow;